// RTK Query type imports available for callers; extraction logic uses duck-typing
// to avoid coupling to the exact RtkQueryError shape.

/**
 * Extract a user-friendly error message from any error shape.
 *
 * Handles: RTK Query errors (data.error.message, data.message, .error string),
 * Error instances, plain objects with .message, strings, null/undefined,
 * and arbitrary values. Never returns "[object Object]".
 *
 * @see extractErrorContext for richer Sentry-oriented extraction.
 */
export function extractErrorMessage(error: unknown, defaultMessage = 'Please try again.'): string {
  // null / undefined / false / 0
  if (!error && error !== 0) return defaultMessage;

  // String → return as-is
  if (typeof error === 'string') return error || defaultMessage;

  try {
    // RTK Query error shape: { status, data: BackendErrorBody }
    if (typeof error === 'object') {
      const obj = error as Record<string, unknown>;

      // RTK Query with unified backend format: { data: { error: { message } } }
      const data = obj.data as Record<string, unknown> | undefined;
      if (data?.error && typeof data.error === 'object' && data.error !== null) {
        const backendError = data.error as Record<string, unknown>;
        if (typeof backendError.message === 'string') {
          return backendError.message;
        }
      }

      // RTK Query with data.message (alternative backend format)
      if (data && typeof data.message === 'string') {
        return data.message;
      }

      // RTK Query with data.error as string
      if (data && typeof data.error === 'string') {
        return data.error;
      }

      // RTK Query network/timeout: { error: "string" }
      if (typeof obj.error === 'string') {
        return obj.error;
      }

      // RTK Query status for context when no message found
      // but only after checking other fields
    }

    // Error instances (including TypeError, RangeError, etc.)
    if (error instanceof Error) return error.message;

    // Plain objects with .message
    if (typeof error === 'object' && error !== null && 'message' in error) {
      const msg = (error as Record<string, unknown>).message;
      if (typeof msg === 'string') return msg;
    }

    // Plain objects without .message → JSON.stringify, truncated
    if (typeof error === 'object' && error !== null) {
      const json = JSON.stringify(error);
      if (json && json !== '{}') {
        return json.length > 500 ? json.slice(0, 500) + '…' : json;
      }
      return defaultMessage;
    }
  } catch {
    // Extraction failed (e.g., circular reference in JSON.stringify)
  }

  // Anything else: try String() with [object Object] guard
  // eslint-disable-next-line no-restricted-syntax -- Intentional: this IS the canonical error extraction utility
  const str = String(error);
  if (str === '[object Object]') return defaultMessage;
  return str || defaultMessage;
}

/**
 * Error context object for Sentry capture — richer than a simple message string.
 */
export interface ErrorContext {
  message: string;
  status?: number | string;
  code?: string;
  originalError: unknown;
}

/**
 * Extract rich error context for Sentry reporting.
 *
 * Returns a structured object with the error message, HTTP status, error code,
 * and a reference to the original error. Use this at Sentry capture sites
 * instead of raw error objects.
 */
export function extractErrorContext(error: unknown): ErrorContext {
  const message = extractErrorMessage(error, 'Unknown error');
  const context: ErrorContext = { message, originalError: error };

  if (error == null || typeof error !== 'object') return context;

  try {
    const obj = error as Record<string, unknown>;

    // Extract status (RTK Query shape)
    if ('status' in obj) {
      context.status = obj.status as number | string;
    }

    // Extract error code from backend format { data: { error: { code } } }
    const data = obj.data as Record<string, unknown> | undefined;
    if (data?.error && typeof data.error === 'object' && data.error !== null) {
      const backendError = data.error as Record<string, unknown>;
      if (typeof backendError.code === 'string') {
        context.code = backendError.code;
      }
    }

    // Extract code from plain objects with .code
    if (!context.code && typeof obj.code === 'string') {
      context.code = obj.code;
    }
  } catch {
    // Context extraction failed — return what we have
  }

  return context;
}
